import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * Toast / Snackbar — transient feedback for save/error/export-started (LAT-14).
 *
 * Replaces every `alert()`, every red error banner, and every "Saved!" inline
 * message in the app. ONE provider mounted at the root; everything else uses
 * the `useToast()` hook.
 *
 * Setup (App.jsx):
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 * Usage anywhere:
 *   const toast = useToast();
 *   toast.success('Customer saved');
 *   toast.error('Could not save', { description: err.message });
 *   toast.info('Export started — check Documents');
 *   toast.warning('Quote will expire in 3 days');
 *   const id = toast.show({ variant: 'info', title: 'Working…', duration: 0 });
 *   toast.dismiss(id);
 *
 * Variants: success | info | warning | danger
 * Auto-dismiss after 4s by default; pass `duration: 0` to keep open until
 * the user dismisses, or `duration: ms` to override.
 */

const ToastContext = createContext(null);

const VARIANT_STYLES = {
  success: {
    icon: CheckCircleIcon,
    // PT-15 #7 (2026-07-24): was `--color-success-fill` (green-600) on
    // `--color-success` (green-700) foreground — adjacent shades, failed
    // 4.5:1 WCAG AA. Swapped to the -bg (50) tint which pairs cleanly with
    // the 700 foreground. Same fix applied to warning below.
    bg: 'var(--color-success-bg)',
    fg: 'var(--color-success)',
    border: 'color-mix(in srgb, var(--color-success) 25%, transparent)',
  },
  info: {
    icon: InformationCircleIcon,
    bg: 'var(--neutral-50)',
    fg: 'var(--neutral-700)',
    border: 'var(--neutral-200)',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    // PT-15 #7 (2026-07-24): was `--color-warning-fill` (amber-600) on
    // `--color-warning` (amber-700) foreground — adjacent shades, failed
    // 4.5:1. Swapped to the -bg (50) tint.
    bg: 'var(--color-warning-bg)',
    fg: 'var(--color-warning)',
    border: 'color-mix(in srgb, var(--color-warning) 30%, transparent)',
  },
  danger: {
    icon: XCircleIcon,
    bg: 'color-mix(in srgb, var(--color-error) 10%, var(--color-surface))',
    fg: 'var(--color-error)',
    border: 'color-mix(in srgb, var(--color-error) 30%, transparent)',
  },
};

let _id = 0;
const nextId = () => ++_id;

export function ToastProvider({ children, defaultDuration = 4000 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (toast) => {
      const id = toast.id ?? nextId();
      const t = {
        id,
        variant: toast.variant || 'info',
        title: toast.title,
        description: toast.description,
        action: toast.action,
        duration: toast.duration ?? defaultDuration,
      };
      setToasts((cur) => {
        // dedupe by id (allow upsert)
        const without = cur.filter((x) => x.id !== id);
        return [...without, t];
      });
      if (t.duration > 0) {
        const timer = setTimeout(() => dismiss(id), t.duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss, defaultDuration]
  );

  // Convenience methods
  const api = useRef(null);
  if (!api.current) {
    api.current = {
      show,
      dismiss,
      success: (title, opts = {}) => show({ ...opts, title, variant: 'success' }),
      info:    (title, opts = {}) => show({ ...opts, title, variant: 'info' }),
      warning: (title, opts = {}) => show({ ...opts, title, variant: 'warning' }),
      error:   (title, opts = {}) => show({ ...opts, title, variant: 'danger' }),
      danger:  (title, opts = {}) => show({ ...opts, title, variant: 'danger' }),
    };
  } else {
    // Keep callbacks fresh
    api.current.show = show;
    api.current.dismiss = dismiss;
  }

  // Cleanup on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback so callers don't crash if the provider is missing in tests
    // — we no-op and warn once in dev.
    if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[useToast] ToastProvider not found in tree — toasts are no-op.');
    }
    const noop = () => 0;
    return {
      show: noop, dismiss: noop,
      success: noop, info: noop, warning: noop, error: noop, danger: noop,
    };
  }
  return ctx;
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 w-full max-w-sm pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }) {
  const styles = VARIANT_STYLES[toast.variant] || VARIANT_STYLES.info;
  const Icon = styles.icon;
  return (
    <div
      role={toast.variant === 'danger' ? 'alert' : 'status'}
      className={clsx(
        'pointer-events-auto flex items-start gap-3 p-3 shadow-lg border',
        'animate-[bidit-toast-in_180ms_ease-out]'
      )}
      style={{
        backgroundColor: styles.bg,
        borderColor: styles.border,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      <Icon
        className="h-5 w-5 flex-shrink-0 mt-0.5"
        style={{ color: styles.fg }}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p
            className="text-sm font-semibold"
            style={{ color: styles.fg }}
          >
            {toast.title}
          </p>
        )}
        {toast.description && (
          <p
            className="mt-0.5 text-sm break-words"
            style={{ color: 'var(--color-text-default)' }}
          >
            {toast.description}
          </p>
        )}
        {toast.action && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => {
                toast.action.onClick?.();
                onDismiss();
              }}
              className="text-sm font-semibold underline hover:no-underline focus:outline-none focus-visible:ring-2 rounded"
              style={{ color: styles.fg }}
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 -mr-1 -mt-1 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-black/5 focus:outline-none focus-visible:ring-2"
        style={{ color: styles.fg }}
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
